import { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import type { EventClickArg } from '@fullcalendar/core';
import type { Publication, PublicationStatus } from '../types';

const STATUS_COLORS: Record<PublicationStatus, string> = {
  'Uncategorized': '#94a3b8',
  'Ideation': '#6b7280',
  'In Progress': '#3b82f6',
  'To Review': '#f59e0b',
  'Approuved': '#14b8a6',
  'Scheduled': '#8b5cf6',
  'Published': '#10b981',
};

interface Props {
  publications: Publication[];
  onEventClick?: (p: Publication) => void;
}

export function PublicationCalendarView({ publications, onEventClick }: Props) {
  const events = useMemo(
    () =>
      publications
        .filter((p) => p.publishDate)
        .map((p) => ({
          id: p.id,
          title: p.titleEn || p.titleFr || '(untitled)',
          start: p.publishDate as string,
          allDay: true,
          backgroundColor: STATUS_COLORS[p.status] ?? '#94a3b8',
          borderColor: STATUS_COLORS[p.status] ?? '#94a3b8',
          extendedProps: { publication: p },
        })),
    [publications],
  );

  const handleEventClick = (arg: EventClickArg) => {
    const p = arg.event.extendedProps.publication as Publication | undefined;
    if (p && onEventClick) onEventClick(p);
  };

  return (
    <div className="bg-white border rounded-lg p-3">
      <FullCalendar
        plugins={[dayGridPlugin, listPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listWeek',
        }}
        events={events}
        eventClick={handleEventClick}
        height="auto"
        firstDay={1}
      />
    </div>
  );
}
